import { ElementRef, inject, Injectable } from '@angular/core';
import { BlockViewData } from '@core/models/catalog-plan.model';
import { CatalogPlanSeatResponseDto, SeatStatusEnumResponseDto } from '@core/services/apiclient';
import { Actions, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import {
  applyPlanFilter as applyPlanFilterAction,
  resetMultiQuickFilter as resetMultiQuickFilterAction,
  updateMultiQuickFilter as updateMultiQuickFilterAction,
} from '@root-store/catalog-plan-store/actions/catalog-plan.actions';
import * as fromCatalogPlan from '@root-store/catalog-plan-store/index';
import { extent, scaleLinear } from 'd3';
import Konva from 'konva';
import { from, Observable, of, startWith, Subject, switchMap, takeUntil, tap, withLatestFrom } from 'rxjs';
import * as _ from 'underscore';
import { SeatBoxSize } from '../models/catalog-plan-constants';
import {
  BulkSelectionScope,
  CanvasProps,
  SeatColorsTypeEnum,
  SeatStatusOptionFilter,
  SelectionType,
} from '../models/catalog-plan.model';
import { isBlockInBulkSelectionScope } from '../utils/bulk-selection-scope.util';
import { CatalogPlanBlockViewCommunicationService } from './catalog-plan-blockview-communication.service';
import { CatalogPlanBlockViewInteractionService } from './catalog-plan-blockview-interaction.service';
import { CatalogPlanBlockViewMinimapService } from './catalog-plan-blockView-minimap.service';
import { CatalogPlanBlockViewUtilsService } from './catalog-plan-blockView-utils.service';

const FREE_SEAT_STATUS_ID = 'Free';

interface FilterOptions {
  hasUnavailable: boolean;
  hasCategories: boolean;
  hasContingents: boolean;
  singleColorFilter: boolean;
  allColorFilters: boolean;
  isOnlyOnePreviousColorFilter: boolean;
  seatColors: SeatColorsTypeEnum[];
}

interface NamesSets {
  unavailableNamesSet: Set<string>;
  categoryNamesSet: Set<string>;
  contingentNamesSet: Set<string>;
  categoryIds: number[];
  contingentIds: number[];
}
@Injectable({
  providedIn: 'root',
})
export class CatalogPlanBlockViewService {
  // props
  private canvasProps: CanvasProps;
  private canvasScaleX: (position: number) => number;
  private canvasScaleY: (position: number) => number;
  private container: ElementRef;
  private minimapContainer: ElementRef;
  private topViewPlan: SVGSVGElement;
  private data: BlockViewData;

  // layers & stage
  // Nullable : null avant le premier createCanvas(), et destroyCanvas() l'y remet pour ne pas
  // exposer un stage detruit. Initialise explicitement, sinon currentStage rendrait `undefined`
  // avant le premier rendu, en contradiction avec son type.
  private stage: Konva.Stage | null = null;
  private statusLayer: Konva.Layer;
  private seatLayer: Konva.Layer;
  private selectionLayer: Konva.Layer;
  private categoryLayer: Konva.Layer;
  private contingentLayer: Konva.Layer;

  private statusDictionary: Map<string, Konva.Group>;
  private categoryDictionary = new Map<string, Konva.Group>();
  private contingentDictionary = new Map<string, Konva.Group>();
  private unavailabilityDictionary = new Map<string, Konva.Group>();

  public destroy$ = new Subject<void>();
  private filterHighlightedSeatIds = new Set<number>();
  private hasSelectionStats = false;
  // Perimetre gele de la selection en masse (cf. BulkSelectionScope). Ce service est un singleton
  // `providedIn: 'root'` : ses champs traversent la navigation entre blocs, d'ou la necessite de savoir
  // si le bloc AFFICHE appartient a la selection avant d'y peindre une surbrillance.
  private bulkSelectionScope: BulkSelectionScope | null = null;
  private selectedSeatIds = new Set<number>();
  // applySeatStatusFilter masque des sous-groupes par place que seul resetFilter() restaure : on
  // memorise donc qu'un filtre statut est actuellement applique sur le plan.
  private hasStatusFilterApplied = false;

  previousFilters = {
    categoryIds: [],
    contingentIds: [],
    unavailableIds: [],
    seatStatusIds: [] as string[],
    seatColors: [SeatColorsTypeEnum.Categories, SeatColorsTypeEnum.Contingents],
  };
  unavailabilityLength: number;
  private readonly utils = inject(CatalogPlanBlockViewUtilsService);
  private readonly minimap = inject(CatalogPlanBlockViewMinimapService);
  public readonly interactionService = inject(CatalogPlanBlockViewInteractionService);
  private readonly store = inject(Store<fromCatalogPlan.State>);
  private readonly communication = inject(CatalogPlanBlockViewCommunicationService);
  private readonly actions = inject(Actions);

  public process(
    blockData: BlockViewData,
    container: ElementRef,
    minimapContainer: ElementRef,
    topviewPlan: d3.Selection<SVGSVGElement, unknown, null, undefined>,
  ) {
    this.container = container;
    this.topViewPlan = topviewPlan && topviewPlan.node();
    this.minimapContainer = minimapContainer;
    this.communication.clearSelection();
    this.data = blockData;
    from(this.utils.init(this.data, SeatBoxSize))
      .pipe(
        tap(() => {
          const elements = this.utils.getAllPlanElements();
          this.canvasProps = this.utils.getCanvasProps(elements);
          this.initScalingFunctions(elements);
          this.createCanvas(container);
          this.render();
        }),
        switchMap(() =>
          this.actions
            .pipe(
              ofType(
                applyPlanFilterAction,
                resetMultiQuickFilterAction,
                updateMultiQuickFilterAction,
              ),
              startWith(null),
            )
            .pipe(
              takeUntil(this.destroy$),
              withLatestFrom(
                this.store.pipe(select(fromCatalogPlan.selectors.selectContingentIdsFilterSelected)),
                this.store.pipe(select(fromCatalogPlan.selectors.selectCategoryIdsFilterSelected)),
                this.store.pipe(select(fromCatalogPlan.selectors.selectUnavailableIdsFilterSelected)),
                this.store.pipe(select(fromCatalogPlan.selectors.selectSeatColorsFilter)),
                this.store.pipe(select(fromCatalogPlan.selectors.selectMultiQuickFilterSelections)),
                this.store.pipe(select(fromCatalogPlan.selectors.selectSeatStatusIdsFilterSelected)),
              ),
            ),
        ),
      )
      .subscribe({
        next: ([
          action,
          contingentIdsMultipleFilter,
          categoryIdsMultipleFilter,
          unavailableIdsMultipleFilter,
          seatColors,
          multiQuickFilter,
          seatStatusIds,
        ]) => {
          const contingentIdsMulti = multiQuickFilter?.selectedContingents?.map((x) => x.id) ?? [];
          const categoryIdsMulti = multiQuickFilter?.selectedCategories?.map((x) => x.id) ?? [];
          const unavailableIdsMulti = multiQuickFilter?.selectedUnavailabilities?.map((x) => x.id) ?? [];
          const hasMulti =
            contingentIdsMulti.length || categoryIdsMulti.length || unavailableIdsMulti.length;

          const contingentIds = hasMulti ? contingentIdsMulti : contingentIdsMultipleFilter ?? [];
          const categoryIds = hasMulti ? categoryIdsMulti : categoryIdsMultipleFilter ?? [];
          const unavailableIds = hasMulti ? unavailableIdsMulti : unavailableIdsMultipleFilter ?? [];

          // Le statut vient de l'axe quick-filter (onglet Statut) s'il est selectionne, sinon on
          // retombe sur le statut du filtre global (dropdown).
          const quickSeatStatusIds = multiQuickFilter?.selectedStatuses?.map((s) => s.id) ?? [];
          const currentSeatStatusIds = quickSeatStatusIds.length ? quickSeatStatusIds : seatStatusIds ?? [];
          if (
            !_.isEqual(contingentIds, this.previousFilters.contingentIds) ||
            !_.isEqual(categoryIds, this.previousFilters.categoryIds) ||
            !_.isEqual(unavailableIds, this.previousFilters.unavailableIds) ||
            !_.isEqual(currentSeatStatusIds, this.previousFilters.seatStatusIds)
          ) {
            setTimeout(() => {
              this.updateSeatVisibility(
                {
                  categoryIds,
                  contingentIds,
                  unavailableIds,
                  seatColors,
                  seatStatusIds: currentSeatStatusIds,
                },
                false,
              );
            }, 500);
            this.previousFilters = {
              categoryIds,
              contingentIds,
              unavailableIds,
              seatStatusIds: currentSeatStatusIds,
              seatColors,
            };
          }
        },
      });
  }

  private initializeLayers() {
    this.seatLayer = new Konva.Layer({ id: 'catalog-seat-layer', listening: true });
    this.statusLayer = new Konva.Layer({ id: 'catalog-status-layer', listening: false });
    this.selectionLayer = new Konva.Layer({ id: 'catalog-selection-layer', listening: true });
    this.categoryLayer = new Konva.Layer({ id: 'catalog-category-layer', listening: true });
    this.contingentLayer = new Konva.Layer({ id: 'catalog-contingent-layer', listening: true });

    this.statusDictionary = new Map<string, Konva.Group>();
    const statusList = Object.values(SeatStatusEnumResponseDto);
    statusList.forEach((status) => {
      const group = new Konva.Group();
      this.statusDictionary.set(status, group);
      this.statusLayer.add(group);
    });
    this.initializeSeatDictionariesFromData();
    this.stage.add(this.seatLayer);
    this.stage.add(this.categoryLayer);
    this.stage.add(this.contingentLayer);
    this.stage.add(this.statusLayer);
    this.stage.add(this.selectionLayer);
    this.seatLayer.setZIndex(0);
    this.categoryLayer.setZIndex(1);
    this.contingentLayer.setZIndex(2);
    this.statusLayer.setZIndex(3);
    this.selectionLayer.setZIndex(4);
  }

  listenToFilterChanges() {
    this.listenToFilterStatusChanges();
    this.listenToFilterColorsChanges();
    // Avant listenToBulkSelectionStats : les deux `select` emettent synchronement a l'abonnement, et le
    // garde-fou de surbrillance a besoin du perimetre AVANT que les stats ne declenchent une peinture.
    this.listenToBulkSelectionScope();
    this.listenToBulkSelectionStats();
    this.listenToSeatSelection();
  }

  private listenToBulkSelectionScope() {
    this.store
      .pipe(select(fromCatalogPlan.selectors.selectBulkSelectionScope), takeUntil(this.destroy$))
      .subscribe({ next: (scope) => (this.bulkSelectionScope = scope) });
  }

  listenToFilterColorsChanges() {
    this.store
      .pipe(select(fromCatalogPlan.selectors.selectSeatColorsFilter), takeUntil(this.destroy$))
      .subscribe({
        next: (seatColors: SeatColorsTypeEnum[]) => {
          let isOnlyOnePreviousColorFilter: boolean;
          if (this.previousFilters.seatColors?.length < 2) {
            isOnlyOnePreviousColorFilter = true;
          }
          if (!_.isEqual(seatColors, this.previousFilters.seatColors)) {
            this.previousFilters.seatColors = seatColors;
            this.updateSeatVisibility(this.previousFilters, true, isOnlyOnePreviousColorFilter);
          }
        },
      });
  }

  listenToFilterStatusChanges() {
    this.store
      .pipe(select(fromCatalogPlan.selectors.selectSeatStatusOptionFilterSelected), takeUntil(this.destroy$))
      .subscribe({
        next: (selectSeatStatusOptionFilter) => {
          this.updateStatusGroupVisibility(selectSeatStatusOptionFilter);
        },
      });
  }

  private listenToBulkSelectionStats() {
    this.store
      .pipe(select(fromCatalogPlan.selectors.selectBulkSelectionStats), takeUntil(this.destroy$))
      .subscribe({
        next: (stats) => {
          const wasActive = this.hasSelectionStats;
          this.hasSelectionStats = !!stats;
          if (!wasActive && this.hasSelectionStats) {
            const { categoryIds, contingentIds, unavailableIds, seatStatusIds, seatColors } = this
              .previousFilters || {
              categoryIds: [],
              contingentIds: [],
              unavailableIds: [],
              seatStatusIds: [],
              seatColors: [],
            };
            const hasAnyFilter =
              (categoryIds?.length || 0) > 0 ||
              (contingentIds?.length || 0) > 0 ||
              (unavailableIds?.length || 0) > 0 ||
              (seatStatusIds?.length || 0) > 0;
            if (hasAnyFilter) {
              this.updateSeatVisibility(
                { categoryIds, contingentIds, unavailableIds, seatStatusIds, seatColors },
                false,
              );
            }
          }
          if (wasActive && !this.hasSelectionStats && this.filterHighlightedSeatIds.size) {
            const toRestore = Array.from(this.filterHighlightedSeatIds)
              .filter((id) => !this.selectedSeatIds.has(id))
              .map((seatId) => this.utils.data?.seats.find((s) => s.seatId === seatId))
              .filter((s): s is CatalogPlanSeatResponseDto => !!s)
              .map((data) => ({ data, node: this.utils.getSeatById(data.seatId), seatId: data.seatId }));
            this.utils.restoreSelectedNodes(toRestore, this.seatLayer);
            this.filterHighlightedSeatIds.clear();
            this.contingentLayer.batchDraw();
          }
        },
      });
  }

  private listenToSeatSelection() {
    this.store
      .pipe(select(fromCatalogPlan.selectors.selectSeatSelection), takeUntil(this.destroy$))
      .subscribe({
        next: (selection) => {
          const ids = new Set<number>((selection || []).map((s) => s.seatId));
          this.selectedSeatIds = ids;
        },
      });
  }

  private createCanvas(canvasWrapper: ElementRef) {
    this.destroyCanvas();
    this.stage = new Konva.Stage({
      container: canvasWrapper.nativeElement,
      width: 400,
      height: 300,
      draggable: this.interactionService.selectionMode === SelectionType.Drag,
      dragBoundFunc: ({ x, y }) => this.utils.defineDragBoundaries({ x, y }),
    });

    this.initializeLayers();
    this.interactionService.init(this.stage, this.container, this.canvasProps, !!this.data?.blockId);
    this.listenToFilterChanges();
  }

  private render() {
    this.interactionService.isPlanDrawingInProgress = true;
    if (this.data?.blockId) {
      this.minimap.init(this.stage, this.minimapContainer, this.topViewPlan);
    }
    this.interactionService.fitStageIntoParentContainer();
    of(this.data?.seats)
      .pipe(takeUntil(this.destroy$), this.renderSeatsAsync())
      .subscribe(() => {
        this.renderLetters();
        this.renderImages();
        this.renderShapes();
        if (this.data?.blockId) {
          this.minimap.cacheMinimapLayer();
        }
        this.interactionService.addStageEventListeners();
        Konva.autoDrawEnabled = true;
        this.interactionService.isPlanDrawingInProgress = false;
        this.communication.init(this.contingentLayer, this.seatLayer);
      });
  }

  public initializeSeatDictionariesFromData() {
    const uniqueContingents = Array.from(new Set(this.data?.seats.map((seat) => seat?.seatContingent?.id)));
    const uniqueCategories = Array.from(new Set(this.data?.seats.map((seat) => seat?.seatCategory?.id)));
    const uniqueUnavailability = Array.from(
      new Set(this.data?.seats.map((seat) => seat?.seatUnavailabilityType?.id).filter((id) => !!id)),
    );
    this.unavailabilityLength = uniqueUnavailability.length;

    uniqueContingents.forEach((contingentId) => {
      const group = new Konva.Group();
      this.contingentDictionary.set(`catalog-seat-contingent-${contingentId}`, group);
      this.contingentLayer.add(group);
    });

    uniqueCategories.forEach((categoryId) => {
      const group = new Konva.Group();
      this.categoryDictionary.set(`catalog-seat-category-${categoryId}`, group);
      this.categoryLayer.add(group);
    });

    uniqueUnavailability.forEach((unavailabilityId) => {
      const group = new Konva.Group();
      this.unavailabilityDictionary.set(`catalog-seat-unavailability-${unavailabilityId}`, group);
      this.contingentLayer.add(group);
    });
  }

  private computeSeatsToHighlight(
    categoryIds: number[],
    contingentIds: number[],
    unavailableIds: number[],
    seatStatusIds: string[],
  ): CatalogPlanSeatResponseDto[] {
    const seats = this.utils?.data?.seats || [];
    const hasSpatialFilter = unavailableIds.length > 0 || categoryIds.length > 0 || contingentIds.length > 0;
    let result: CatalogPlanSeatResponseDto[];
    if (unavailableIds.length > 0) {
      result = seats.filter(
        (s) => s?.seatUnavailabilityType?.id != null && unavailableIds.includes(s.seatUnavailabilityType.id),
      );
    } else if (categoryIds.length > 0 && contingentIds.length > 0) {
      result = seats.filter(
        (s) => categoryIds.includes(s?.seatCategory?.id) && contingentIds.includes(s?.seatContingent?.id),
      );
    } else if (categoryIds.length > 0) {
      result = seats.filter((s) => categoryIds.includes(s?.seatCategory?.id));
    } else if (contingentIds.length > 0) {
      result = seats.filter((s) => contingentIds.includes(s?.seatContingent?.id));
    } else {
      result = seatStatusIds.length > 0 ? [...seats] : [];
    }
    if (seatStatusIds.length > 0) {
      result = result.filter((s) => {
        // In status-only mode (no spatial filter), unavailability seats must not be selected
        // via the synthetic 'Free' status — they are catalog-blocked, not genuinely available.
        // Match them against their actual order status array only.
        if (!hasSpatialFilter && s?.seatUnavailabilityType?.id != null) {
          return s.status?.some((statusId) => seatStatusIds.includes(statusId));
        }
        return this.seatMatchesAnyStatusFilter(s, seatStatusIds);
      });
    }
    return result;
  }

  public updateSeatVisibility(
    filters: {
      categoryIds?: number[];
      contingentIds?: number[];
      unavailableIds?: number[];
      seatStatusIds?: string[];
      seatColors?: SeatColorsTypeEnum[];
    },
    fromColorFiltre?: boolean,
    isOnlyOnePreviousColorFilter?: boolean,
  ) {
    const { categoryIds, contingentIds, unavailableIds, seatStatusIds, seatColors } = filters;
    if (
      (!categoryIds || categoryIds.length === 0) &&
      (!contingentIds || contingentIds.length === 0) &&
      (!unavailableIds || unavailableIds.length === 0) &&
      (!seatStatusIds || seatStatusIds.length === 0) &&
      !fromColorFiltre
    ) {
      this.resetFilter();
      return;
    }

    const hasCategories = categoryIds?.length > 0;
    const hasContingents = contingentIds?.length > 0;
    const hasUnavailable = unavailableIds?.length > 0;
    const hasOtherFilters = hasCategories || hasContingents || hasUnavailable;

    // applySeatStatusFilter masque des sous-groupes par place (catalog-seat-group-*) et seul
    // resetFilter() les restaure : handleShapeProcessing ne travaille que sur les shapes. Des qu'un
    // filtre statut est applique, on repart donc d'un plan propre avant de re-filtrer, sinon les
    // places masquees le restent quand on enchaine sur un axe contingent/categorie/indispo.
    if (!fromColorFiltre && (this.hasStatusFilterApplied || (!hasOtherFilters && seatStatusIds?.length))) {
      this.resetFilter();
    }

    if (hasOtherFilters || fromColorFiltre) {
      const contingentNamesSet = new Set(contingentIds.map((id) => `catalog-seat-contingent-${id}`) || []);
      const categoryNamesSet = new Set(categoryIds.map((id) => `catalog-seat-category-${id}`) || []);
      const unavailableNamesSet = new Set(
        unavailableIds.map((id) => `catalog-seat-unavailability-${id}`) || [],
      );

      const singleColorFilter = seatColors.length < 2;
      const allColorFilters = seatColors.length === 2;

      const filterOptions: FilterOptions = {
        hasUnavailable,
        hasCategories,
        hasContingents,
        singleColorFilter,
        allColorFilters,
        isOnlyOnePreviousColorFilter,
        seatColors,
      };

      const namesSets: NamesSets = {
        unavailableNamesSet,
        categoryNamesSet,
        contingentNamesSet,
        categoryIds,
        contingentIds,
      };

      this.handleShapeProcessing(filterOptions, namesSets);
    }

    if (seatStatusIds?.length) {
      this.applySeatStatusFilter(seatStatusIds);
    }

    // Ne peindre la surbrillance de selection QUE si le bloc affiche appartient reellement a la
    // selection gelee. Sans ce garde-fou, toute application de filtre pendant qu'une selection existe
    // surlignait les places filtrees du bloc COURANT — donc en entrant dans un autre bloc, ses places
    // au meme filtre apparaissaient « selectionnees » alors qu'elles ne le sont pas, et en nombre
    // different de celui annonce par le compteur. `hasSelectionStats` reste `true` d'un bloc a l'autre
    // (service singleton, destroyCanvas() ne le reinitialise pas), d'ou la propagation.
    // Un perimetre a blockId nul (selection prise en vue top) reste plan-wide : la surbrillance est
    // alors legitime dans tous les blocs. Cf. isBlockInBulkSelectionScope et son spec.
    if (this.hasSelectionStats && isBlockInBulkSelectionScope(this.bulkSelectionScope, this.data?.blockId)) {
      const seats = this.computeSeatsToHighlight(
        categoryIds || [],
        contingentIds || [],
        unavailableIds || [],
        seatStatusIds || [],
      ).filter((s) => !this.utils.isSeatLocked(s));
      const newIds = new Set<number>(seats.map((s) => s.seatId));

      const toRestoreIds: number[] = Array.from(this.filterHighlightedSeatIds)
        .filter((id) => !newIds.has(id))
        .filter((id) => !this.selectedSeatIds.has(id));
      const toHighlightIds: number[] = Array.from(newIds).filter(
        (id) => !this.filterHighlightedSeatIds.has(id),
      );

      if (toRestoreIds.length) {
        const toRestore = toRestoreIds
          .map((id) => this.utils.data?.seats.find((s) => s.seatId === id))
          .filter((s): s is CatalogPlanSeatResponseDto => !!s)
          .map((data) => ({ data, node: this.utils.getSeatById(data.seatId), seatId: data.seatId }));
        this.utils.restoreSelectedNodes(toRestore, this.seatLayer);
      }
      if (toHighlightIds.length) {
        const toHighlight = toHighlightIds
          .map((id) => this.utils.data?.seats.find((s) => s.seatId === id))
          .filter((s): s is CatalogPlanSeatResponseDto => !!s)
          .map((data) => ({ data, node: this.utils.getSeatById(data.seatId), seatId: data.seatId }));
        this.utils.highlightSelectedNodes(toHighlight, this.seatLayer);
      }
      this.filterHighlightedSeatIds = newIds;
      this.contingentLayer.batchDraw();
    }
  }

  private getShapesByConditions(
    group: Konva.Group,
    allowedClasses: string[],
    excludedIdSubstring: string,
    includeAdditionalCondition?: (node: Konva.Node) => boolean,
  ): Konva.Shape[] {
    return group.find((node) => {
      const matchesClass = allowedClasses.includes(node.getClassName());
      const doesNotIncludeId = !node.id().includes(excludedIdSubstring);
      const passesAdditionalCondition = includeAdditionalCondition ? includeAdditionalCondition(node) : true;

      return matchesClass && doesNotIncludeId && passesAdditionalCondition;
    });
  }

  private getShapes(group: Konva.Group): Konva.Shape[] {
    return this.getShapesByConditions(group, ['Rect', 'Circle'], 'catalog-seat-highlighter');
  }

  private getUnavailabilityAllShapes(group: Konva.Group): Konva.Shape[] {
    return this.getShapesByConditions(group, ['Rect', 'Circle', 'Text'], 'catalog-seat-highlighter');
  }

  private handleShapeProcessing(filterOptions: FilterOptions, namesSets: NamesSets) {
    const {
      hasUnavailable,
      hasCategories,
      hasContingents,
      singleColorFilter,
      allColorFilters,
      isOnlyOnePreviousColorFilter,
      seatColors,
    } = filterOptions;
    const { unavailableNamesSet, categoryNamesSet, contingentNamesSet } = namesSets;
    if (hasUnavailable) {
      this.processUnavailableShapes(unavailableNamesSet);
    } else {
      if (hasCategories && hasContingents) {
        this.processMixedShapes(categoryNamesSet, contingentNamesSet);
      } else if (hasCategories) {
        this.processCategoryShapes(categoryNamesSet);
      } else if (hasContingents) {
        this.processContingentShapes(contingentNamesSet);
      }
      if (singleColorFilter) {
        this.processSeatColorsShapes(seatColors, false, hasCategories || hasContingents);
      }
      if (allColorFilters && isOnlyOnePreviousColorFilter) {
        this.processSeatColorsShapes(seatColors, true, hasCategories || hasContingents);
      }
    }
  }

  private processUnavailableShapes(unavailableNamesSet: Set<string>) {
    this.categoryDictionary.forEach((categoryGroup) => {
      categoryGroup.visible(false);
    });

    this.contingentDictionary.forEach((contingentGroup) => {
      const contingentShapes = this.getShapes(contingentGroup);
      contingentShapes.forEach((shape) => {
        const shouldBeVisible = shape.name() === 'highlighter-selected';
        shape.visible(shouldBeVisible);
      });
    });

    if (this.unavailabilityLength > 0) {
      this.unavailabilityDictionary.forEach((unavailabilityGroup, unavailabilityId) => {
        const unavailabilityShapes = this.getUnavailabilityAllShapes(unavailabilityGroup);
        unavailabilityShapes.forEach((shape) => {
          const shouldBeVisible = unavailableNamesSet.has(unavailabilityId);
          shape.visible(shouldBeVisible);
        });
      });
    }
  }

  private processMixedShapes(categoryNamesSet: Set<string>, contingentNamesSet: Set<string>) {
    const matchedCategoryShapes = new Map<string, Konva.Shape>();
    const matchedContingentShapes = new Map<string, Konva.Shape>();

    this.categoryDictionary.forEach((categoryGroup, categoryId) => {
      categoryGroup.visible(true);
      if (categoryNamesSet.has(categoryId)) {
        const categoryShapes = this.getShapes(categoryGroup);
        categoryShapes.forEach((shape) => {
          matchedCategoryShapes.set(shape.id(), shape);
        });
      } else {
        categoryGroup.visible(false);
      }
    });
    this.contingentDictionary.forEach((contingentGroup, contingentId) => {
      contingentGroup.visible(true);
      const contingentShapes = this.getShapes(contingentGroup);
      contingentShapes.forEach((shape) => {
        const shouldBeVisible =
          contingentNamesSet.has(contingentId) || shape.name() === 'highlighter-selected';
        shape.visible(shouldBeVisible);
        if (contingentNamesSet.has(contingentId)) {
          matchedContingentShapes.set(shape.id(), shape);
        }
      });
    });

    matchedCategoryShapes.forEach((shape, id) => {
      const isMatch = matchedContingentShapes.has(id);
      shape.visible(isMatch);
    });

    matchedContingentShapes.forEach((shape, id) => {
      const isMatch = matchedCategoryShapes.has(id);
      if (shape.name() === 'highlighter-selected') {
        shape.visible(true);
      } else {
        shape.visible(isMatch);
      }
    });

    this.processUnavailabilityShapes(false);
  }

  private processContingentShapes(contingentNamesSet: Set<string>) {
    const visibleContingentShapeIds = new Set<string>();
    this.contingentDictionary.forEach((contingentGroup, contingentId) => {
      contingentGroup.visible(true);
      const contingentShapes = this.getShapes(contingentGroup);
      contingentShapes.forEach((shape) => {
        const shouldBeVisible =
          contingentNamesSet.has(contingentId) || shape.name() === 'highlighter-selected';
        shape.visible(shouldBeVisible);
        if (shouldBeVisible && contingentNamesSet.has(contingentId)) {
          visibleContingentShapeIds.add(shape.id());
        }
      });
    });

    this.categoryDictionary.forEach((categoryGroup) => {
      categoryGroup.visible(true);
      const categoryShapes = this.getShapes(categoryGroup);
      categoryShapes.forEach((categoryShape) => {
        if (visibleContingentShapeIds.has(categoryShape.id())) {
          categoryShape.visible(true);
        } else {
          categoryShape.visible(false);
        }
      });
    });
    this.processUnavailabilityShapes(false);
  }

  private processCategoryShapes(categoryNamesSet: Set<string>) {
    const visibleCategoryShapeIds = new Set<string>();
    this.categoryDictionary.forEach((categoryGroup, categoryId) => {
      if (categoryNamesSet.has(categoryId)) {
        categoryGroup.visible(true);
        const categoryShapes = this.getShapes(categoryGroup);
        categoryShapes.forEach((shape) => {
          shape.visible(true);
          visibleCategoryShapeIds.add(shape.id());
        });
      } else {
        categoryGroup.visible(false);
      }
    });

    this.contingentDictionary.forEach((contingentGroup) => {
      contingentGroup.visible(true);
      const contingentShapes = this.getShapes(contingentGroup);
      contingentShapes.forEach((contingentShape: Konva.Shape) => {
        if (
          visibleCategoryShapeIds.has(contingentShape.id()) ||
          contingentShape.name() === 'highlighter-selected'
        ) {
          contingentShape.visible(true);
        } else {
          contingentShape.visible(false);
        }
      });
    });

    this.processUnavailabilityShapes(false);
  }

  private processSeatColorsShapes(
    seatColors: SeatColorsTypeEnum[],
    resetAll?: boolean,
    isOtherFilters?: boolean,
  ) {
    const setVisible = (
      dictionary: Map<string, Konva.Group>,
      visible: boolean,
      isContingent: boolean,
      forceVisible = false,
    ) => {
      dictionary.forEach((group) => {
        if (!group.visible() && visible && isOtherFilters) {
          return;
        }
        if (isContingent) {
          const contingentShapes = this.getShapes(group);
          contingentShapes.forEach((shape) => {
            const isSelected = shape.name() === 'highlighter-selected';
            if (!isSelected && shape.visible()) {
              shape.opacity(visible ? 1 : 0);
            } else if (!isSelected && forceVisible && !isOtherFilters) {
              shape.opacity(1);
            }
          });
        } else {
          group.visible(visible);
        }
      });
    };
    if (resetAll) {
      setVisible(this.categoryDictionary, true, false, true);
      setVisible(this.contingentDictionary, true, true, true);
    } else {
      const showCategories = seatColors.includes(SeatColorsTypeEnum.Categories);

      setVisible(this.categoryDictionary, showCategories, false);
      setVisible(this.contingentDictionary, !showCategories, true);
    }
    this.categoryLayer.batchDraw();
    this.contingentLayer.batchDraw();
  }

  private processUnavailabilityShapes(shouldBeVisible: boolean) {
    if (this.unavailabilityLength > 0) {
      this.unavailabilityDictionary.forEach((group) => {
        const shapes = this.getUnavailabilityAllShapes(group);
        shapes.forEach((shape) => shape.visible(shouldBeVisible));
      });
    }
  }

  private resetFilter() {
    const makeAllShapesVisible = (group: Konva.Group, key: 'contingent' | 'category') => {
      const shapes = this.getShapes(group);
      shapes.forEach((shape: Konva.Shape) => {
        shape.visible(true);
        if (key === 'contingent') {
          shape.opacity(1);
        }
      });
    };

    this.categoryDictionary.forEach((categoryGroup) => {
      makeAllShapesVisible(categoryGroup, 'category');
      categoryGroup.visible(true);
      categoryGroup.find('Group').forEach((g) => g.visible(true));
    });

    this.contingentDictionary.forEach((contingentGroup) => {
      makeAllShapesVisible(contingentGroup, 'contingent');
      contingentGroup.visible(true);
      contingentGroup.find('Group').forEach((g) => g.visible(true));
    });

    this.unavailabilityDictionary.forEach((unavailabilityGroup) => {
      const contingentShapes = this.getUnavailabilityAllShapes(unavailabilityGroup);
      contingentShapes.forEach((shape) => {
        shape.visible(true);
      });
      unavailabilityGroup.visible(true);
      unavailabilityGroup.find('Group').forEach((g) => g.visible(true));
    });

    this.statusDictionary?.forEach((group) => {
      group.find('Group').forEach((g) => g.visible(true));
    });

    this.hasStatusFilterApplied = false;
    this.batchDrawLayer();
  }

  private getEffectiveSeatStatusIds(seat: CatalogPlanSeatResponseDto): string[] {
    return seat.status?.length ? seat.status : [FREE_SEAT_STATUS_ID];
  }

  private seatMatchesAnyStatusFilter(seat: CatalogPlanSeatResponseDto, seatStatusIds: string[]): boolean {
    return this.getEffectiveSeatStatusIds(seat).some((s) => seatStatusIds.includes(s));
  }

  private applySeatStatusFilter(seatStatusIds: string[]): void {
    if (!this.data?.seats) return;

    this.data.seats.forEach((seat) => {
      const matchesStatus = this.seatMatchesAnyStatusFilter(seat, seatStatusIds);

      if (!matchesStatus) {
        const seatGroupId = `catalog-seat-group-${seat.seatId}`;
        // Hide category and contingent overlays (seat appears greyed/disabled like other filters)
        this.categoryDictionary.forEach((group) => {
          const node = group.findOne(`#${seatGroupId}`);
          if (node) node.visible(false);
        });
        this.contingentDictionary.forEach((group) => {
          const node = group.findOne(`#${seatGroupId}`);
          if (node) node.visible(false);
        });
        if (this.unavailabilityLength > 0) {
          this.unavailabilityDictionary.forEach((group) => {
            const node = group.findOne(`#${seatGroupId}`);
            if (node) node.visible(false);
          });
        }
        // Hide status icons
        this.statusDictionary?.forEach((group) => {
          const node = group.findOne(`#${seatGroupId}`);
          if (node) node.visible(false);
        });
      }
    });
    this.hasStatusFilterApplied = true;
    this.batchDrawLayer();
  }

  private batchDrawLayer() {
    this.seatLayer.batchDraw();
    this.contingentLayer.batchDraw();
    this.categoryLayer.batchDraw();
    this.statusLayer.batchDraw();
  }

  private updateStatusGroupVisibility(filter: SeatStatusOptionFilter) {
    this.statusDictionary?.forEach((group) => {
      const images = group.find('Image');
      if (images?.length) {
        images.forEach((image: Konva.Image) => {
          switch (image.name()) {
            case SeatStatusEnumResponseDto.Edited:
              image.visible(filter.showEdited);
              break;
            case SeatStatusEnumResponseDto.Subscribed:
              image.visible(filter.showSubscribed);
              break;
            case SeatStatusEnumResponseDto.Paid:
              image.visible(filter.showPaid);
              break;
            case SeatStatusEnumResponseDto.Reserved:
              image.visible(filter.showReserved);
              break;
            case SeatStatusEnumResponseDto.Locked:
              image.visible(filter.showLocked);
              break;
            case SeatStatusEnumResponseDto.Controlled:
              image.visible(filter.showControlled);
              break;
          }
        });
      }
    });
  }

  private initScalingFunctions(elements: { x: number; y: number; [key: string]: any }[]) {
    const scale = this.utils.getInitialScale(this.container, this.canvasProps);
    const widthTranslateRatio =
      ((this.container?.nativeElement?.offsetWidth - 8) / scale - this.canvasProps?.width) / 2;
    this.canvasScaleX = scaleLinear()
      .domain(extent(elements, (element) => element?.x))
      .range([widthTranslateRatio, widthTranslateRatio + this.canvasProps?.width]);

    this.canvasScaleY = scaleLinear()
      .domain(extent(elements, (element) => element?.y))
      .range([0, 0 + this.canvasProps?.height]);
  }

  private renderSeatsAsync() {
    Konva.autoDrawEnabled = false;
    return (source$: Observable<CatalogPlanSeatResponseDto[]>) => {
      return source$.pipe(
        takeUntil(this.destroy$),
        tap((seats: CatalogPlanSeatResponseDto[]) => {
          const seatGroup = this.utils.createCatalogPlanSeats(
            seats,
            this.canvasScaleX,
            this.canvasScaleY,
            this.statusDictionary,
            this.categoryDictionary,
            this.contingentDictionary,
            this.unavailabilityDictionary,
            this.categoryLayer,
            this.contingentLayer,
          );
          this.seatLayer.add(seatGroup);
        }),
      );
    };
  }

  private getChangedSeats(updatedSeats: CatalogPlanSeatResponseDto[]): CatalogPlanSeatResponseDto[] {
    return updatedSeats.filter((updatedSeat) => {
      const currentSeat = this.utils.data.seats.find((seat) => seat.seatId == updatedSeat.seatId);
      if (!currentSeat) {
        return false;
      }
      return (
        currentSeat.seatCategory?.id !== updatedSeat.seatCategory?.id ||
        currentSeat.seatContingent?.id !== updatedSeat.seatContingent?.id ||
        (currentSeat.seatUnavailabilityType?.id || null) !==
          (updatedSeat.seatUnavailabilityType?.id || null) ||
        !this.areArraysEqual(currentSeat.status, updatedSeat.status)
      );
    });
  }

  public refreshBlockViewSeats(updatedSeats: CatalogPlanSeatResponseDto[]) {
    const changes = this.getChangedSeats(updatedSeats);
    if (!changes) {
      return;
    }

    changes.forEach((updatedSeat) => {
      const existingSeat = this.utils.data.seats.find((seat) => seat.seatId == updatedSeat.seatId);
      const changes = {
        isSeatLocked: this.utils.isSeatLocked(updatedSeat),
        isCategoryChanged: existingSeat.seatCategory?.id !== updatedSeat.seatCategory?.id,
        isContingentChanged: existingSeat.seatContingent?.id !== updatedSeat.seatContingent?.id,
        isUnavailabilityChanged: existingSeat.seatUnavailabilityType !== updatedSeat.seatUnavailabilityType,
        isStatusChanged: !this.areArraysEqual(existingSeat.status, updatedSeat.status),
      };
      if (changes.isSeatLocked) {
        const seat: CatalogPlanSeatResponseDto =
          changes.isCategoryChanged || changes.isContingentChanged ? existingSeat : updatedSeat;
        this.handleLockedSeat(seat, existingSeat, changes);
      } else {
        this.handleUnlockedSeat(updatedSeat, existingSeat);
      }
    });

    this.updateSeatData(changes);
    this.updateSeatVisibility(this.previousFilters, true);
    this.communication.refreshSeatSelectionNodes(changes);
  }

  private handleLockedSeat(
    seat: CatalogPlanSeatResponseDto,
    existingSeat: CatalogPlanSeatResponseDto,
    changes,
  ) {
    this.removeSeatsFromDictionaries(seat);

    if (changes.isStatusChanged) {
      this.removeStatusFromDictionary(existingSeat);
    }

    if (seat.seatUnavailabilityType) {
      this.removeUnavailabilityFromDictionary(seat);
    }

    const node = this.seatLayer.findOne(`#catalog-seat-group-${seat?.seatId}`);
    if (node && node instanceof Konva.Group) {
      this.utils.drawSeatIcons(seat, node, this.statusDictionary);
      this.seatLayer.batchDraw();
    }
  }

  private handleUnlockedSeat(
    updatedSeat: CatalogPlanSeatResponseDto,
    existingSeat: CatalogPlanSeatResponseDto,
  ) {
    this.removeStatusFromDictionary(existingSeat);
    this.removeUnavailabilityFromDictionary(existingSeat);
    this.removeSeatsFromDictionaries(existingSeat);
    this.addSeatToPlan(updatedSeat);
  }

  private areArraysEqual(arr1: any[], arr2: any[]): boolean {
    if (arr1 === arr2) return true;
    if (!arr1 || !arr2) return false;
    if (arr1.length !== arr2.length) return false;

    return arr1.every((item, index) => item === arr2[index]);
  }

  private addSeatToPlan(seat: CatalogPlanSeatResponseDto) {
    this.utils.createNewSeatInDictionaries(
      {
        seat,
        scaleX: this.canvasScaleX,
        scaleY: this.canvasScaleY,
      },
      this.categoryDictionary,
      this.contingentDictionary,
      this.statusDictionary,
      this.unavailabilityDictionary,
      this.categoryLayer,
      this.contingentLayer,
    );
    this.batchDrawLayer();
  }

  private removeSeatsFromDictionaries(seat: CatalogPlanSeatResponseDto) {
    const categoryGroup = this.categoryDictionary.get(`catalog-seat-category-${seat.seatCategory?.id}`);
    if (categoryGroup) {
      const seatGroupCategory = categoryGroup.findOne(`#catalog-seat-group-${seat.seatId}`);
      if (seatGroupCategory) {
        seatGroupCategory.destroy();
      }
    }

    const contingentGroup = this.contingentDictionary.get(
      `catalog-seat-contingent-${seat.seatContingent?.id}`,
    );
    if (contingentGroup) {
      const seatGroupContingent = contingentGroup.findOne(`#catalog-seat-group-${seat.seatId}`);
      if (seatGroupContingent) {
        seatGroupContingent.destroy();
        const seatNumber: Konva.Label = this.seatLayer.findOne(`#catalog-seat-number-${seat.seatId}`);
        if (seatNumber) {
          seatNumber.getTag().setAttrs({
            fill: 'transparent',
          });
        }
      }
    }
  }

  private removeUnavailabilityFromDictionary(seat: CatalogPlanSeatResponseDto) {
    const group = this.unavailabilityDictionary.get(
      `catalog-seat-unavailability-${seat.seatUnavailabilityType?.id}`,
    );
    if (group) {
      const seatGroupUnavailability = group.findOne(`#catalog-seat-group-${seat?.seatId}`);
      if (seatGroupUnavailability) {
        seatGroupUnavailability.destroy();
      }
    }
  }

  private removeStatusFromDictionary(existingSeat: CatalogPlanSeatResponseDto) {
    existingSeat?.status?.forEach((status) => {
      const statusGroup = this.statusDictionary.get(status);
      if (statusGroup) {
        const seatGroupId = `catalog-seat-group-${existingSeat.seatId}`;
        const seatGroup = statusGroup.findOne(`#${seatGroupId}`);
        if (seatGroup) {
          seatGroup.destroy();
        }
      }
    });
    this.statusLayer.batchDraw();
  }

  updateSeatData(seatsChanged: CatalogPlanSeatResponseDto[]) {
    this.utils.data = {
      ...this.utils.data,
      seats: this.utils.data.seats.map((seatData) => {
        const seatWithStatusChange = seatsChanged.find(
          (changedSeat) => changedSeat.seatId == seatData.seatId,
        );
        return seatWithStatusChange || seatData;
      }),
    };
  }
  /**
   * adds letters to canvas
   */
  private renderLetters() {
    if (this.data.letters?.length) {
      this.data.letters.forEach((letter) => {
        const letterElement = this.utils.drawLetter(letter, this.canvasScaleX, this.canvasScaleY);
        this.seatLayer.add(letterElement);
        letterElement.moveToBottom();
      });
    }
  }

  /**
   * adds images to canvas
   */
  private renderImages() {
    if (this.data.imageData) {
      this.data.imageData?.imageDatas?.forEach((imageData) => {
        const imageContent = this.data.imageData?.imageContents.find(
          (content) => imageData?.imageContentId === content?.imageContentId,
        );
        const image = new Image();
        const extension = imageContent?.extension === 'UNKNOWN' ? 'png' : imageContent.extension;
        image.src = `data:image/${extension};base64,${imageContent?.image}`;
        image.onload = () => {
          const imageElement = this.utils.drawImage(imageData, image, this.canvasScaleX, this.canvasScaleY);
          this.seatLayer.add(imageElement);
          imageElement.moveToBottom();
        };
      });
    }
  }

  /**
   * adds shapes to canvas
   */
  private renderShapes() {
    if (this.data.shapes?.length) {
      this.data.shapes.forEach((shape) => {
        const shapeElement = this.utils.drawShape(shape, this.canvasScaleX, this.canvasScaleY);

        this.seatLayer.add(shapeElement);

        shapeElement.moveToBottom();
      });
    }
  }

  destroyCanvas() {
    if (this.stage) {
      this.stage.destroy();
      if (this.data?.blockId) {
        this.minimap.destroyMinimap();
      }
      this.destroy$.next();
      this.interactionService.destroy();
      this.communication.destroy();
      this.categoryDictionary.clear();
      this.contingentDictionary.clear();
      this.unavailabilityDictionary.clear();
      // Un stage detruit ne doit plus etre expose (cf. currentStage, utilise par l'export PNG).
      this.stage = null;
    }
    this.previousFilters = {
      categoryIds: [],
      contingentIds: [],
      unavailableIds: [],
      seatStatusIds: [],
      seatColors: [SeatColorsTypeEnum.Categories, SeatColorsTypeEnum.Contingents],
    };
    this.hasStatusFilterApplied = false;
  }

  public requestSeatDetails() {
    return this.interactionService.seatDetailsRequested$;
  }

  /**
   * Expose le stage pour l'export PNG de la vue bloc (cf. CatalogPlanExportService).
   * `null` entre la destruction du plan et le rendu du suivant : l'appelant doit s'en garder.
   */
  public get currentStage(): Konva.Stage | null {
    return this.stage;
  }

  /**
   * Vrai tant que le plan n'est pas entierement dessine — l'exporter maintenant produirait un PNG
   * aux places manquantes.
   *
   * Il faut l'union des deux signaux, aucun ne suffit seul :
   * - `isPlanDrawingInProgress` couvre le debut de `render()`, mais il est remis a false des la fin
   *   du bloc synchrone, alors que `batchDrawProcess` a delegue les places a des Web Workers qui
   *   n'ont pas encore repondu ;
   * - le loader ne passe a `show` qu'a la reponse du premier worker, il ne couvre donc pas ce debut,
   *   mais il est le seul a rester vrai jusqu'a la livraison du dernier.
   */
  public get isPlanBeingDrawn(): boolean {
    return this.interactionService.isPlanDrawingInProgress || this.utils.isDrawingSeats;
  }
}
